<script setup lang="ts">
import AppLayout from '../../layouts/AppLayout.vue'
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuth } from '../../composables/useAuth'
import { useCurrentContext } from '../../composables/useCurrentContext'
import { useUserStore } from '../../stores/user'
import { API_CONFIG } from '../../config/api'

const route = useRoute()
const router = useRouter()
const { supplierId } = useCurrentContext()
const userStore = useUserStore()
// Promotions can be managed by any claimed supplier, including FREE tier (the
// backend's promotions routes aren't tier-gated); admins puppeting bypass.
const canEditPromos = computed(() => userStore.isImpersonating || !!userStore.supplierTier)
const { getAccessTokenSilently } = useAuth()
const loading = ref(true)
const promotions = ref<any[]>([])
const isActive = (promo: any) => !promo.valid_until || new Date(promo.valid_until) > new Date()
const endMs = (promo: any) => promo.valid_until ? new Date(promo.valid_until).getTime() : Number.MAX_SAFE_INTEGER
// Active: ending soonest first, no end date last. Expired: most recently expired first.
const activePromos = computed(() => promotions.value.filter(isActive).sort((a, b) => endMs(a) - endMs(b)))
const expiredPromos = computed(() => promotions.value.filter(p => !isActive(p)).sort((a, b) => endMs(b) - endMs(a)))
const tab = ref<'live' | 'expired'>('live')
const promoQuery = ref('')
// Search matches name, description, TSD name and product names within the current tab.
const matchesQuery = (promo: any, q: string) =>
    [promo.name, promo.description, promo.tsd_name, ...(promo.products || []).map((p: { name: string }) => p.name)]
        .some(s => typeof s === 'string' && s.toLowerCase().includes(q))
const shownPromos = computed(() => {
    const list = tab.value === 'live' ? activePromos.value : expiredPromos.value
    const q = promoQuery.value.trim().toLowerCase()
    return q ? list.filter(p => matchesQuery(p, q)) : list
})
const showModal = ref(false)
const isEditing = ref(false)
const editingId = ref<string | null>(null)

const form = ref({
    name: '',
    description: '',
    scope: 'PRODUCT',
    tsd_name: '',
    valid_until: ''
})

// Product picker for PRODUCT scope — the backend rejects PRODUCT promos with no
// product_ids. null = still loading.
const products = ref<{ id: string; name: string }[] | null>(null)
const selectedProductIds = ref<string[]>([])
const productError = ref('')
const nameError = ref('')
const descriptionError = ref('')
const productQuery = ref('')
const filteredProducts = computed(() => {
    const q = productQuery.value.trim().toLowerCase()
    return (products.value || []).filter(p => !q || p.name.toLowerCase().includes(q))
})
// Select all / Clear act on the visible (filtered) rows only.
function selectVisibleProducts() {
    selectedProductIds.value = [...new Set([...selectedProductIds.value, ...filteredProducts.value.map(p => p.id)])]
    productError.value = ''
}
function clearVisibleProducts() {
    const visible = new Set(filteredProducts.value.map(p => p.id))
    selectedProductIds.value = selectedProductIds.value.filter(id => !visible.has(id))
}
const saveError = ref('')
const saving = ref(false)
const pageNotice = ref('')

async function fetchProducts() {
    try {
        const token = await getAccessTokenSilently()
        const res = await fetch(`${API_CONFIG.baseURL}/api/suppliers/${supplierId.value}/products`, {
            headers: { Authorization: `Bearer ${token}` }
        })
        const pj = res.ok ? await res.json() : []
        products.value = (Array.isArray(pj) ? pj : pj.products || []).map((x: any) => ({ id: x.id, name: x.name }))
    } catch (e) {
        console.error(e)
        products.value = []
    }
}

// TSD Name suggestions — tsd_name stays free text, the list only helps people
// type a consistent name.
const tsdNames = ref<string[]>([])
const tsdOpen = ref(false)
const tsdActive = ref(-1)
const tsdSuggestions = computed(() => {
    const q = form.value.tsd_name.trim().toLowerCase()
    return tsdNames.value.filter(n => !q || n.toLowerCase().includes(q)).slice(0, 8)
})
const showUseTypedTsd = computed(() => {
    const q = form.value.tsd_name.trim().toLowerCase()
    return !!q && !tsdNames.value.some(n => n.toLowerCase() === q)
})

let tsdsLoading = false
async function fetchTsds() {
    if (tsdsLoading) return
    tsdsLoading = true
    try {
        const token = await getAccessTokenSilently()
        const res = await fetch(`${API_CONFIG.baseURL}/api/suppliers/${supplierId.value}/tsds/available`, {
            headers: { Authorization: `Bearer ${token}` }
        })
        const tj = res.ok ? await res.json() : []
        if (!res.ok) console.error(`[promotions] TSD list failed (${res.status})`)
        tsdNames.value = (Array.isArray(tj) ? tj : tj.tsds || []).map((x: any) => x.name).filter(Boolean)
    } catch (e) {
        console.error(e)
    } finally {
        tsdsLoading = false
    }
}

function pickTsd(name: string) {
    form.value.tsd_name = name
    tsdOpen.value = false
    tsdActive.value = -1
}

function onTsdKeydown(e: KeyboardEvent) {
    const n = tsdSuggestions.value.length
    if (e.key === 'ArrowDown') {
        e.preventDefault()
        tsdOpen.value = true
        tsdActive.value = n ? (tsdActive.value + 1) % n : -1
    } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        tsdActive.value = n ? (tsdActive.value - 1 + n) % n : -1
    } else if (e.key === 'Enter' && tsdOpen.value && tsdActive.value >= 0 && tsdActive.value < n) {
        e.preventDefault()
        pickTsd(tsdSuggestions.value[tsdActive.value]!)
    } else if (e.key === 'Escape') {
        tsdOpen.value = false
    }
}

async function fetchPromos() {
    loading.value = true;
    try {
        const token = await getAccessTokenSilently()
        const res = await fetch(`${API_CONFIG.baseURL}/api/suppliers/${supplierId.value}/promotions`, {
            headers: { Authorization: `Bearer ${token}` }
        })
        const data = await res.json()
        // A 403/500 returns an error object, not a list; keep the list a list.
        promotions.value = res.ok && Array.isArray(data) ? data : []
    } catch (e) {
        console.error(e)
    } finally {
        loading.value = false
    }
}

// ── Promo sheet (PDF) ──
const sheetUploadingId = ref<string | null>(null)
async function uploadSheet(promo: any, ev: Event) {
    const input = ev.target as HTMLInputElement
    const file = input.files?.[0]
    input.value = ''
    if (!file) return
    sheetUploadingId.value = promo.id
    try {
        const token = await getAccessTokenSilently()
        const fd = new FormData()
        fd.append('file', file)
        const res = await fetch(`${API_CONFIG.baseURL}/api/suppliers/${supplierId.value}/promotions/${promo.id}/sheet`, {
            method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
        })
        if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.error || 'Upload failed'); return }
        await fetchPromos()
    } catch (e) { console.error(e); alert('Upload failed') }
    finally { sheetUploadingId.value = null }
}
async function viewSheet(promo: any) {
    try {
        const token = await getAccessTokenSilently()
        const res = await fetch(`${API_CONFIG.baseURL}/api/suppliers/${supplierId.value}/promotions/${promo.id}/sheet`, { headers: { Authorization: `Bearer ${token}` } })
        const j = await res.json()
        if (j.url) window.open(j.url, '_blank')
    } catch (e) { console.error(e) }
}
async function removeSheet(promo: any) {
    if (!confirm(`Remove sheet "${promo.sheet_file_name}"?`)) return
    try {
        const token = await getAccessTokenSilently()
        await fetch(`${API_CONFIG.baseURL}/api/suppliers/${supplierId.value}/promotions/${promo.id}/sheet`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } })
        await fetchPromos()
    } catch (e) { console.error(e) }
}

function openCreateModal() {
    isEditing.value = false
    editingId.value = null
    currentEditPromo.value = null
    modalSheetFile.value = null
    form.value = {
        name: '',
        description: '',
        scope: 'PRODUCT',
        tsd_name: '',
        valid_until: ''
    }
    selectedProductIds.value = []
    productError.value = ''
    nameError.value = ''
    descriptionError.value = ''
    productQuery.value = ''
    saveError.value = ''
    showModal.value = true
}

const currentEditPromo = ref<any>(null)
function openEditModal(promo: any) {
    isEditing.value = true
    editingId.value = promo.id
    currentEditPromo.value = promo
    modalSheetFile.value = null
    form.value = {
        name: promo.name,
        description: promo.description,
        scope: promo.scope,
        tsd_name: promo.tsd_name || '',
        valid_until: (promo.valid_until ? new Date(promo.valid_until).toISOString().split('T')[0] : '') as string
    }
    selectedProductIds.value = (promo.products || []).map((p: { id: string }) => p.id)
    productError.value = ''
    nameError.value = ''
    descriptionError.value = ''
    productQuery.value = ''
    saveError.value = ''
    showModal.value = true
}

// PDF sheet picked inside the modal — uploaded right after the promo saves
// (a sheet needs a promotion id, so create-mode uploads happen post-create).
// Dropzone with a programmatic input.click(): drag & drop or click to browse.
const modalSheetFile = ref<File | null>(null)
const sheetInputRef = ref<HTMLInputElement | null>(null)
const dragOver = ref(false)
const sheetError = ref('')

function acceptSheetFile(file: File | undefined | null) {
    sheetError.value = ''
    if (!file) return
    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
    if (!isPdf) { sheetError.value = 'Promo sheets must be PDF files.'; return }
    if (file.size > 25 * 1024 * 1024) { sheetError.value = 'Max file size is 25 MB.'; return }
    modalSheetFile.value = file
}

function onModalSheetPick(ev: Event) {
    const input = ev.target as HTMLInputElement
    acceptSheetFile(input.files?.[0])
    input.value = ''
}

function onSheetDrop(ev: DragEvent) {
    dragOver.value = false
    acceptSheetFile(ev.dataTransfer?.files?.[0])
}

async function savePromo() {
    saveError.value = ''
    nameError.value = form.value.name.trim() ? '' : 'Add a promo name.'
    descriptionError.value = form.value.description.trim() ? '' : 'Add a description.'
    productError.value = form.value.scope === 'PRODUCT' && selectedProductIds.value.length === 0 ? 'Pick at least one product' : ''
    const firstInvalid = nameError.value ? 'promo-name' : descriptionError.value ? 'promo-description' : productError.value ? 'promo-product-search' : ''
    if (firstInvalid) { document.getElementById(firstInvalid)?.focus(); return }
    saving.value = true
    try {
        const token = await getAccessTokenSilently()
        const url = isEditing.value
            ? `${API_CONFIG.baseURL}/api/suppliers/${supplierId.value}/promotions/${editingId.value}`
            : `${API_CONFIG.baseURL}/api/suppliers/${supplierId.value}/promotions`

        const method = isEditing.value ? 'PUT' : 'POST'

        const res = await fetch(url, {
            method: method,
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                ...form.value,
                product_ids: form.value.scope === 'PRODUCT' ? selectedProductIds.value : []
            })
        })
        const saved = await res.json().catch(() => null)
        if (!res.ok) { saveError.value = saved?.error || `Failed to save promotion (${res.status})`; return }
        const promoId = isEditing.value ? editingId.value : saved?.id
        if (modalSheetFile.value && promoId) {
            const fd = new FormData()
            fd.append('file', modalSheetFile.value)
            const up = await fetch(`${API_CONFIG.baseURL}/api/suppliers/${supplierId.value}/promotions/${promoId}/sheet`, {
                method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
            })
            if (!up.ok) { const j = await up.json().catch(() => ({})); pageNotice.value = `Promo saved, but the PDF upload failed${j.error ? `: ${j.error}` : ''}` }
        }
        modalSheetFile.value = null
        showModal.value = false;
        fetchPromos();
    } catch(e) {
        console.error(e)
        saveError.value = "Couldn't save the promotion. Try again."
    } finally {
        saving.value = false
    }
}

async function deletePromo(id: string) {
    if (!confirm('Are you sure you want to delete this promotion?')) return

    try {
        const token = await getAccessTokenSilently()
        const res = await fetch(`${API_CONFIG.baseURL}/api/suppliers/${supplierId.value}/promotions/${id}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}` }
        })
        if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.error || `Failed to delete (${res.status})`); return }
        fetchPromos()
    } catch(e) {
        alert('Error deleting promotion')
    }
}

// On a hard reload the user store resolves after mount, so supplierId starts
// null; wait for it instead of calling /api/suppliers/null/… (403).
function loadAll() { fetchPromos(); fetchProducts(); fetchTsds() }
watch(supplierId, id => { if (id) loadAll() })

onMounted(() => {
    if (supplierId.value) loadAll()

    // Auto-open create modal if ?create=true query param is present
    if (route.query.create === 'true') {
        openCreateModal()
        // Clear the query param to prevent re-opening on refresh
        router.replace({ path: route.path, query: {} })
    }
})

const formatDate = (d: string) => new Date(d).toLocaleDateString()
</script>

<template>
  <AppLayout>
    <div class="space-y-6">
       <div class="flex justify-between items-center">
        <div>
           <h1 class="text-2xl font-bold text-cc-text">Promotions</h1>
           <p class="text-cc-text-secondary">Your campaigns and spiffs.</p>
        </div>
        <button v-if="canEditPromos" @click="openCreateModal" class="bg-cc-primary text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-cc-primary-hover">
            + New Promo
        </button>
      </div>

      <div v-if="pageNotice" class="flex items-start justify-between gap-3 px-4 py-3 rounded-lg border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-800 text-sm text-red-600 dark:text-red-400">
        <span>{{ pageNotice }}</span>
        <button type="button" @click="pageNotice = ''" class="text-xs hover:underline shrink-0">Dismiss</button>
      </div>

      <div v-if="loading" class="text-center py-12 text-gray-500">Loading promotions...</div>

      <div v-else class="space-y-4">
        <div v-if="promotions.length" class="flex items-end justify-between gap-4 border-b border-cc-border">
            <div class="flex gap-1" role="tablist">
                <button v-for="[k, l, n] in ([['live', 'Live', activePromos.length], ['expired', 'Expired', expiredPromos.length]] as const)" :key="k"
                    type="button" role="tab" :aria-selected="tab === k" @click="tab = k"
                    class="px-4 py-2 text-sm font-medium -mb-px border-b-2 transition"
                    :class="tab === k ? 'border-cc-primary text-cc-text' : 'border-transparent text-cc-text-tertiary hover:text-cc-text-secondary'">
                    {{ l }} <span class="opacity-60">{{ n }}</span>
                </button>
            </div>
            <div class="relative mb-2 w-full max-w-xs">
                <svg class="w-4 h-4 text-cc-text-tertiary absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-4.35-4.35M17 11a6 6 0 11-12 0 6 6 0 0112 0z"/></svg>
                <input v-model="promoQuery" type="search" placeholder="Search promotions" aria-label="Search promotions"
                    class="w-full pl-9 pr-3 py-1.5 rounded-lg border border-cc-border-med bg-cc-elevated text-sm text-cc-text placeholder:text-cc-text-tertiary focus:outline-none focus:ring-2 focus:ring-cc-primary/40" />
            </div>
        </div>
        <div v-if="promotions.length && !shownPromos.length" class="text-center py-8 text-cc-text-tertiary bg-cc-elevated rounded border border-dashed border-cc-border">
            {{ promoQuery.trim() ? `No ${tab} promotions match "${promoQuery.trim()}"` : `No ${tab} promotions` }}
        </div>
        <div v-for="promo in shownPromos" :key="promo.id" class="bg-cc-elevated p-6 rounded-lg border border-cc-border shadow-sm flex flex-col md:flex-row gap-6 relative overflow-hidden group">
            <!-- Active Indicator -->
            <div class="absolute left-0 top-0 bottom-0 w-1 bg-green-500" v-if="isActive(promo)"></div>
            <div class="absolute left-0 top-0 bottom-0 w-1 bg-gray-300" v-else></div>

            <div class="flex-1">
                <div class="flex items-center gap-2 mb-1">
                   <h3 class="text-lg font-bold text-cc-text">{{ promo.name }}</h3>
                   <span v-if="isActive(promo)" class="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded uppercase font-bold">Active</span>
                   <span v-else class="text-[10px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded uppercase font-bold">Expired</span>
                   
                   <span class="text-[10px] px-2 py-0.5 rounded uppercase font-bold border ml-2" 
                    :class="{
                        'bg-blue-50 text-blue-700 border-blue-200': promo.scope === 'PRODUCT',
                        'bg-indigo-50 text-indigo-700 border-indigo-200': promo.scope === 'SUPPLIER',
                        'bg-amber-50 text-amber-700 border-amber-200': promo.scope === 'TSD'
                    }">
                       {{ promo.scope === 'TSD' && promo.tsd_name ? `TSD: ${promo.tsd_name}` : promo.scope }}
                   </span>
                </div>
                <p class="text-cc-text-secondary">{{ promo.description }}</p>
                <div class="mt-4 flex flex-wrap gap-2">
                    <span v-for="p in promo.products" :key="p.id" class="text-xs bg-gray-50 dark:bg-gray-700 border border-gray-100 dark:border-gray-600 px-2 py-1 rounded text-cc-text-secondary">
                        {{ p.name }}
                    </span>
                </div>
                <!-- Promo sheet (PDF) -->
                <div class="mt-3 flex flex-wrap items-center gap-2">
                    <template v-if="promo.sheet_file_name">
                        <button @click="viewSheet(promo)" class="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-cc-border bg-cc-surface text-xs font-semibold text-cc-primary hover:underline">
                            📄 {{ promo.sheet_file_name }}
                        </button>
                        <button v-if="canEditPromos" @click="removeSheet(promo)" class="text-xs text-cc-text-tertiary hover:text-red-500">remove</button>
                    </template>
                    <label v-if="canEditPromos" class="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-dashed border-cc-border-med text-xs font-semibold text-cc-text-secondary hover:text-cc-primary hover:border-cc-primary cursor-pointer transition-colors">
                        {{ sheetUploadingId === promo.id ? 'Uploading…' : (promo.sheet_file_name ? 'Replace PDF' : '📎 Attach PDF sheet') }}
                        <input type="file" accept=".pdf,application/pdf" class="hidden" :disabled="sheetUploadingId === promo.id" @change="e => uploadSheet(promo, e)" />
                    </label>
                </div>
            </div>
            
            <div class="shrink-0 flex flex-col items-end justify-between">
                <div class="text-sm">
                    <div class="text-cc-text-secondary text-right">Valid Until</div>
                    <div class="font-medium text-cc-text text-right">{{ promo.valid_until ? formatDate(promo.valid_until) : 'Ongoing' }}</div>
                </div>

                <!-- Actions -->
                <div v-if="canEditPromos" class="flex gap-2 mt-4 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button @click="openEditModal(promo)" class="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg" title="Edit">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"/></svg>
                    </button>
                    <button @click="deletePromo(promo.id)" class="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg" title="Delete">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                    </button>
                </div>
            </div>
        </div>

        <div v-if="promotions.length === 0" class="text-center py-8 text-gray-400 bg-cc-elevated rounded border border-dashed border-cc-border">
            No promotions created yet.
        </div>
      </div>

      <!-- Create/Edit Modal -->
      <div v-if="showModal" class="fixed inset-0 bg-black/50 z-50 overflow-y-auto">
        <div class="min-h-full flex items-center justify-center p-4">
          <div class="bg-cc-elevated rounded-xl shadow-xl w-full max-w-lg p-5 sm:p-6">
              <h2 class="text-xl font-bold mb-4 dark:text-white">{{ isEditing ? 'Edit Promotion' : 'Create New Promotion' }}</h2>
              <form @submit.prevent="savePromo" novalidate class="space-y-4">
                  <div>
                      <label for="promo-name" class="block text-sm font-medium text-cc-text-secondary mb-1">Promo Name <span class="text-cc-danger">*</span></label>
                      <input id="promo-name" v-model="form.name" @input="nameError = ''" :aria-invalid="nameError ? 'true' : undefined" :aria-describedby="nameError ? 'promo-name-error' : undefined"
                          class="w-full px-3 py-2 rounded-lg border bg-white dark:bg-gray-900 text-cc-text" :class="nameError ? 'border-cc-danger/50' : 'border-cc-border-med'" />
                      <p v-if="nameError" id="promo-name-error" class="text-xs text-cc-danger mt-1">{{ nameError }}</p>
                  </div>
                  <div>
                      <label for="promo-description" class="block text-sm font-medium text-cc-text-secondary mb-1">Description <span class="text-cc-danger">*</span></label>
                      <textarea id="promo-description" v-model="form.description" @input="descriptionError = ''" :aria-invalid="descriptionError ? 'true' : undefined" :aria-describedby="descriptionError ? 'promo-description-error' : undefined"
                          rows="2" class="w-full px-3 py-2 rounded-lg border bg-white dark:bg-gray-900 text-cc-text" :class="descriptionError ? 'border-cc-danger/50' : 'border-cc-border-med'"></textarea>
                      <p v-if="descriptionError" id="promo-description-error" class="text-xs text-cc-danger mt-1">{{ descriptionError }}</p>
                  </div>
                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                          <label for="promo-scope" class="block text-sm font-medium text-cc-text-secondary mb-1">Scope</label>
                          <select id="promo-scope" v-model="form.scope" class="w-full px-3 py-2 rounded-lg border border-cc-border-med bg-white dark:bg-gray-900 text-cc-text">
                              <option value="PRODUCT">Product Specific</option>
                              <option value="SUPPLIER">All Products (Supplier Wide)</option>
                              <option value="TSD">TSD Specific</option>
                          </select>
                      </div>
                      <div v-if="form.scope === 'TSD'" class="relative">
                          <label for="tsd-name" class="block text-sm font-medium text-cc-text-secondary mb-1">TSD Name</label>
                          <input id="tsd-name" v-model="form.tsd_name" placeholder="e.g. Avant" autocomplete="off"
                              role="combobox" aria-autocomplete="list" aria-controls="tsd-suggestions" :aria-expanded="tsdOpen"
                              :aria-activedescendant="tsdOpen && tsdActive >= 0 ? `tsd-opt-${tsdActive}` : undefined"
                              @focus="tsdOpen = true; if (!tsdNames.length) fetchTsds()" @input="tsdOpen = true; tsdActive = -1" @blur="tsdOpen = false" @keydown="onTsdKeydown"
                              class="w-full px-3 py-2 rounded-lg border border-cc-border-med bg-white dark:bg-gray-900 text-cc-text" />
                          <ul v-if="tsdOpen && (tsdSuggestions.length || showUseTypedTsd)" id="tsd-suggestions" role="listbox"
                              class="absolute z-10 left-0 right-0 mt-1 max-h-56 overflow-y-auto rounded-lg border border-cc-border-med bg-cc-elevated shadow-lg py-1">
                              <li v-for="(n, i) in tsdSuggestions" :id="`tsd-opt-${i}`" :key="n" role="option" :aria-selected="i === tsdActive"
                                  @mousedown.prevent="pickTsd(n)" @mouseenter="tsdActive = i"
                                  class="px-3 py-1.5 text-sm cursor-pointer truncate transition-colors"
                                  :class="i === tsdActive ? 'bg-cc-primary-subtle text-cc-text' : 'text-cc-text-secondary'">
                                  {{ n }}
                              </li>
                              <li v-if="showUseTypedTsd" @mousedown.prevent="tsdOpen = false"
                                  class="px-3 py-1.5 text-xs text-cc-text-tertiary cursor-pointer hover:bg-cc-hover"
                                  :class="{ 'border-t border-cc-border mt-1 pt-2': tsdSuggestions.length }">
                                  Use “{{ form.tsd_name.trim() }}” as typed
                              </li>
                          </ul>
                      </div>
                  </div>
                  <div v-if="form.scope === 'PRODUCT'">
                      <div class="flex items-baseline justify-between mb-1">
                          <label class="block text-sm font-medium text-cc-text-secondary">Products <span class="text-cc-danger">*</span></label>
                          <span v-if="products?.length" class="text-xs text-cc-text-tertiary">{{ selectedProductIds.length }} of {{ products.length }} selected</span>
                      </div>
                      <div v-if="products === null" class="rounded-lg border border-cc-border-med px-3 py-4 text-center text-xs text-cc-text-tertiary">Loading products…</div>
                      <div v-else-if="products.length === 0" class="rounded-lg border border-dashed border-cc-border-med px-3 py-4 text-center">
                          <p class="text-sm text-cc-text-secondary">No products yet — choose 'All Products' scope instead</p>
                      </div>
                      <div v-else class="rounded-lg border overflow-hidden transition-colors" :class="productError ? 'border-cc-danger/50' : 'border-cc-border-med'">
                          <div class="flex items-center gap-2 px-3 py-2 border-b border-cc-border bg-cc-surface">
                              <template v-if="products.length > 6">
                                  <svg class="w-4 h-4 text-cc-text-tertiary shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-4.35-4.35M17 11a6 6 0 11-12 0 6 6 0 0112 0z"/></svg>
                                  <input id="promo-product-search" v-model="productQuery" @keydown.enter.prevent type="search" placeholder="Search products" aria-label="Search products" :aria-describedby="productError ? 'promo-products-error' : undefined" class="flex-1 min-w-0 bg-transparent text-sm text-cc-text placeholder:text-cc-text-tertiary outline-none" />
                              </template>
                              <span v-else class="flex-1 text-xs text-cc-text-tertiary">Applies to the products you tick</span>
                              <button type="button" @click="selectVisibleProducts" :disabled="!filteredProducts.length" class="text-xs font-semibold text-cc-primary hover:underline disabled:opacity-40 disabled:no-underline shrink-0">Select all</button>
                              <span class="text-cc-text-tertiary text-xs">·</span>
                              <button type="button" @click="clearVisibleProducts" :disabled="!selectedProductIds.length" class="text-xs font-semibold text-cc-text-secondary hover:text-cc-text disabled:opacity-40 shrink-0">Clear</button>
                          </div>
                          <ul class="max-h-48 overflow-y-auto py-1">
                              <li v-for="p in filteredProducts" :key="p.id">
                                  <label class="relative flex items-center gap-2.5 px-3 py-1.5 text-sm cursor-pointer transition-colors"
                                      :class="selectedProductIds.includes(p.id) ? 'bg-cc-primary-subtle text-cc-text' : 'text-cc-text-secondary hover:bg-cc-hover'">
                                      <input type="checkbox" :value="p.id" v-model="selectedProductIds" @change="productError = ''" class="peer sr-only" />
                                      <span class="w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-cc-primary/40"
                                          :class="selectedProductIds.includes(p.id) ? 'bg-cc-primary border-cc-primary' : 'bg-cc-elevated border-cc-border-med'">
                                          <svg v-if="selectedProductIds.includes(p.id)" class="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/></svg>
                                      </span>
                                      <span class="truncate">{{ p.name }}</span>
                                  </label>
                              </li>
                              <li v-if="!filteredProducts.length" class="px-3 py-4 text-center text-xs text-cc-text-tertiary">No products match "{{ productQuery }}"</li>
                          </ul>
                      </div>
                      <p v-if="productError" id="promo-products-error" class="text-xs text-cc-danger mt-1">{{ productError }}</p>
                  </div>
                  <div>
                      <label for="promo-end-date" class="block text-sm font-medium text-cc-text-secondary mb-1">End Date</label>
                      <input id="promo-end-date" type="date" v-model="form.valid_until" class="w-full px-3 py-2 rounded-lg border border-cc-border-med bg-white dark:bg-gray-900 text-cc-text dark:[color-scheme:dark]" />
                  </div>
                  <div>
                      <label class="block text-sm font-medium text-cc-text-secondary mb-1">Promo sheet (PDF) <span class="font-normal text-cc-text-tertiary">· optional — advisors can download it from this promotion</span></label>
                      <div v-if="isEditing && currentEditPromo?.sheet_file_name && !modalSheetFile" class="flex items-center gap-2 text-sm mb-1.5">
                          <span class="text-cc-text">📄 {{ currentEditPromo.sheet_file_name }}</span>
                          <span class="text-xs text-cc-text-tertiary">(pick a new file below to replace it)</span>
                      </div>
                      <div
                          @click="sheetInputRef?.click()"
                          @dragover.prevent="dragOver = true"
                          @dragleave.prevent="dragOver = false"
                          @drop.prevent="onSheetDrop"
                          class="rounded-lg border-2 border-dashed px-4 py-5 text-center cursor-pointer transition-colors select-none"
                          :class="dragOver ? 'border-cc-primary bg-cc-primary/5' : 'border-cc-border-med hover:border-cc-primary/60'"
                      >
                          <template v-if="modalSheetFile">
                              <p class="text-sm font-medium text-cc-text">📄 {{ modalSheetFile.name }}</p>
                              <p class="text-xs text-cc-text-tertiary mt-0.5">Will upload when you {{ isEditing ? 'update' : 'create' }} the promotion ·
                                  <button type="button" @click.stop="modalSheetFile = null" class="text-red-500 hover:underline">remove</button>
                              </p>
                          </template>
                          <template v-else>
                              <p class="text-sm text-cc-text-secondary">Drag &amp; drop a PDF here, or <span class="text-cc-primary font-semibold">click to browse</span></p>
                              <p class="text-xs text-cc-text-tertiary mt-0.5">Up to 25 MB</p>
                          </template>
                      </div>
                      <input ref="sheetInputRef" type="file" accept=".pdf,application/pdf" class="hidden" @change="onModalSheetPick" />
                      <p v-if="sheetError" class="text-xs text-red-500 mt-1">{{ sheetError }}</p>
                  </div>

                  <p v-if="saveError" class="text-sm text-red-500">{{ saveError }}</p>
                  <div class="flex justify-end gap-3 mt-6">
                      <button type="button" @click="showModal = false" class="px-4 py-2 text-cc-text-secondary hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">Cancel</button>
                      <button type="submit" :disabled="saving" class="px-4 py-2 bg-cc-primary text-white rounded-lg hover:bg-cc-primary-hover disabled:opacity-50 disabled:cursor-not-allowed">{{ saving ? 'Saving…' : (isEditing ? 'Update' : 'Create') }}</button>
                  </div>
              </form>
          </div>
        </div>
      </div>
    </div>
  </AppLayout>
</template>
